import React from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { ButtonBase } from "@mui/material";

import { DragAndDropListHook } from "commons/hooks/useDragAndDropList";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import LineSeparator from "commons/ui/LineSeparator";
import Paper from "commons/ui/Paper";

import { BlocksListItem } from "hooks/form/useBlocksForm";

import { blockEnumMap } from "utils/blockEnumMap";

type FocusType =
    | {
          previewImageStyle: {
              outline: string;
              boxShadow: string;
          };
          actualImageStyle: {
              opacity: string;
          };
      }
    | {
          previewImageStyle?: undefined;
          actualImageStyle?: undefined;
      };

type PropsType = {
    setDraggable: React.Dispatch<React.SetStateAction<boolean>>;
    blockGroupComposition: BlocksListItem[];
    dragAndDropHook: DragAndDropListHook<unknown>;
    draggable: boolean;
};

function PreviewImage({
    setDraggable,
    draggable,
    divProps,
    focusStyle,
    img,
}: {
    setDraggable: React.Dispatch<React.SetStateAction<boolean>>;
    draggable: boolean;
    divProps: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    img: React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;
    focusStyle: FocusType;
}) {
    return (
        <div
            {...divProps}
            draggable={draggable}
            className={"preview-image"}
            style={focusStyle.previewImageStyle}
        >
            <ButtonBase className={"drag-indicator-icon"} onMouseDown={() => setDraggable(true)}>
                <DragIndicatorIcon />
            </ButtonBase>
            {img}
        </div>
    );
}

function BlocksPreview({
    setDraggable,
    blockGroupComposition,
    dragAndDropHook,
    draggable,
}: PropsType) {
    const [showPreview, setShowPreview] = React.useState<boolean>(false);

    // show it if there are element and they are all set not to be deleted
    React.useEffect(() => {
        setShowPreview(
            blockGroupComposition.length !== 0 &&
                !blockGroupComposition.every((block) => block.deleted === true),
        );
    }, [blockGroupComposition]);

    return (
        <>
            {showPreview && (
                <Paper className={"module-preview"}>
                    <GridColumn>
                        <GridRow>
                            <GridItem className={"preview-title"}>
                                <h2>Ordine blocchi</h2>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <div className={"preview-container"}>
                        {blockGroupComposition.map((singleBlock, index) => {
                            if (singleBlock.deleted) {
                                return <></>;
                            }

                            const focusStyle = singleBlock.focusedBlock
                                ? {
                                      previewImageStyle: {
                                          outline: "2px solid rgb(56, 56, 56)",
                                          boxShadow: "0 0 15px rgba(56, 56, 56, 0.5)",
                                      },
                                      actualImageStyle: {
                                          opacity: "1",
                                          borderLeft: "2px solid rgb(56, 56, 56)",
                                      },
                                  }
                                : {};

                            const blockId = singleBlock.item.block_id;

                            const divProps: React.DetailedHTMLProps<
                                React.HTMLAttributes<HTMLDivElement>,
                                HTMLDivElement
                            > = { className: "" };

                            if (dragAndDropHook !== undefined) {
                                divProps.className +=
                                    " " + dragAndDropHook.classes[index]?.join(" ");

                                divProps.onDragStart = () => dragAndDropHook.onDragStart(index);
                                divProps.onDragEnd = () => dragAndDropHook.onDragEnd(index);
                                divProps.onDragOver = (event) => dragAndDropHook.onDragOver(event);
                                divProps.onDragEnter = () => dragAndDropHook.onDragEnter(index);
                                divProps.onDragLeave = () => dragAndDropHook.onDragLeave(index);
                                divProps.onDrop = () => {
                                    dragAndDropHook.onDrop(index);

                                    setDraggable(false);
                                };
                            }

                            return (
                                <GridColumn key={singleBlock.item.id}>
                                    <GridRow justifyContent={"center"}>
                                        <GridItem>
                                            <PreviewImage
                                                divProps={divProps}
                                                draggable={draggable}
                                                focusStyle={focusStyle}
                                                setDraggable={setDraggable}
                                                img={
                                                    <img
                                                        src={blockEnumMap[blockId].img}
                                                        alt={blockEnumMap[blockId].title}
                                                        style={focusStyle.actualImageStyle}
                                                        className={"actual-image"}
                                                    />
                                                }
                                            />
                                        </GridItem>
                                    </GridRow>
                                </GridColumn>
                            );
                        })}
                    </div>
                </Paper>
            )}
        </>
    );
}

export default BlocksPreview;
